// The option list behind ONE purpose's model picker (#5201, epic #5200 D9) — which
// models the org's own keys can serve for that pin, how healthy each is, and in what
// order to show them.
//
// PURPOSE-SCOPED, not agent-scoped. A pin is per purpose, so a picker is too: the Coder
// owns four purposes (`general` plus the three task-tag rows) and gets four pickers.
// `AGENT_ROSTER.purpose` names each picker's owner — the `triage` purpose's picker is
// the TRIAGE agent's, and the keys it offers are the GENERAL lane's
// (`laneForPinPurpose`), so the same general-fenced key reads `reservedFor: 'triage'`
// there and `reservedFor: 'coder'` in the Coder's own general picker: the owner label
// follows the PICKER, so the card's badge and the reserved-for label name one agent.
//
// IT OFFERS WHAT THE RESOLVER WOULD SPEND, through the resolver's own predicates, taken
// from the `llm-credential` barrel and never re-spelled here:
//   - the LANE FENCE is `laneAdmits`. A key carrying a `purpose` contributes only to the
//     picker of exactly that lane, so a key fenced `quick` never shows under `general`,
//     and no fenced key shows under `default` or `escalation`, which are not lanes;
//   - a PARKED catalogue row is out via `modelActive` (`active ?? true`), the key's own
//     model included, as `keyServesModel` refuses it.
// Where the two differ, the picker is the conservative one: a `default` or `escalation`
// pin is served under the task's OWN lane at claim time, so a fenced key can serve it
// there, yet this list hides fenced keys from those pickers. It never offers a key the
// resolver will not spend.
//
// INPUT is the org keys list as `GET /v1/billing/keys` serves it (personal keys never
// reach it: that route excludes them in SQL). The fields are snake_case because they ARE
// that wire, so the page hands its list over with no adapter. `cooldown_until_iso` is
// the per-key reset #5203 adds; a row without one reads as "reset unknown".
//
// Health, per key:
//   valid                            selectable
//   rate_limited, credits_exhausted  selectable, with the soonest known reset
//   unverified, invalid              listed last, as `unavailable`
//   revoked                          not offered at all
//
// ORDER: the tier group first — Smart/Smartest (heavy AND smart) above general, fast
// and untiered — then the health band, then provider, then display name; the
// unavailable rows last. Every offer, selectable or unavailable, carries the reasoning
// capability its catalogue entries reported, so each row can be badged.
//
// Pure: the keys and the pin in, the ordered list out.

import {
  type BYOKKeyKind,
  type BYOKProvider,
  BYOKStatus,
  type BYOKTier,
  plausibleContextLength,
} from '../entities/byok';
import { AGENT_ROSTER, type AgentId } from './agent-roster';
import { type LlmRoutePurposeAny, laneForPinPurpose } from './job-class-routing';
import { detectedModelRates, laneAdmits, modelActive } from './llm-credential';
import { modelDisplayName } from './model-display-name';
import type { ReasoningCapability } from './reasoning-capability';
import type { RouteRow } from './route-row';

/** One catalogue row of a picker key — `byok_key_models` as the keys wire carries it. */
export type PickerKeyModel = {
  readonly model: string;
  readonly tier?: BYOKTier | null;
  /** Absent = active, the resolver's default (`modelActive`). */
  readonly active?: boolean;
  readonly context_length?: number | null;
  readonly input_per_mtok?: number | null;
  readonly output_per_mtok?: number | null;
  /**
   * The reasoning capability the provider reports for this model, or a one-time probe
   * proved (see {@link ReasoningCapability}, `value-objects/reasoning-capability.ts`).
   * Read by the offer's `reasoning` field so the picker can render a per-row
   * capability badge — a detected `supported: true` means the model reasons, not
   * that any one key was set up to send it. The first contribution that carries one
   * wins; a row of all-`null` contributions answers `null` (unknown, the same
   * "nothing was detected" reading the dashboard's badge treats as off).
   */
  readonly reasoning?: ReasoningCapability | null;
};

/** One ORG key as the keys wire serves it — only the fields the picker reads. */
export type PickerKey = {
  readonly provider: BYOKProvider;
  readonly model: string;
  readonly status: BYOKStatus;
  /** Billing nature; null on a legacy row never classified. */
  readonly kind?: BYOKKeyKind | null;
  /** The raw stored lane fence; null = unfenced. An unrecognised value fences to nothing. */
  readonly purpose?: string | null;
  /** When a spent or throttled key comes back; absent or null = unknown. */
  readonly cooldown_until_iso?: string | null;
  /** Absent or empty = exactly {@link PickerKey.model}. */
  readonly models?: readonly PickerKeyModel[];
};

/**
 * The key states a picker lets a person choose. `rate_limited` and `credits_exhausted`
 * are among them because they are TEMPORARY: the lane waits for such a key rather than
 * running on another one (#5758), so a person may pick it. A PIN naming one is refused
 * by the shared verdict (`keyPinVerdict`, `../entities/key-outage.ts`) — only a key the
 * live pool would serve now is a candidate.
 */
export type SelectableHealth = 'valid' | 'rate_limited' | 'credits_exhausted';
/** The key states a picker shows but will not let a person choose. */
export type UnavailableHealth = 'unverified' | 'invalid';

/** A selectable `(provider, model)`, merged across every key that serves it. */
export type ModelOffer = {
  readonly kind: 'model';
  readonly provider: BYOKProvider;
  readonly model: string;
  readonly displayName: string;
  /** Selectable keys serving this pair. */
  readonly keyCount: number;
  readonly bestHealth: SelectableHealth;
  /** The soonest a spent key serving it comes back; null when one is valid now, or unknown. */
  readonly soonestResetIso: string | null;
  /** The most common tier tag its keys give it; a tie goes to the stronger tier. */
  readonly tier: BYOKTier | null;
  readonly kinds: readonly BYOKKeyKind[];
  /** The smallest plausible window a key reports; null when none does. */
  readonly contextLength: number | null;
  /** The cheapest complete rate card a key reports; null when none does. */
  readonly price: { readonly inputPerMtok: number; readonly outputPerMtok: number } | null;
  /**
   * The reasoning capability any contributing catalogue entry reported — the picker
   * renders a per-row badge on `reasoning.supported === true`. Null when no entry
   * carried one (unknown), or when every entry said `supported: null` / `false`.
   */
  readonly reasoning: ReasoningCapability | null;
  /**
   * The agent this picker belongs to, when some key behind the offer is fenced to its
   * lane — the PICKER's owner (`AGENT_ROSTER.purpose`), never the lane's. The `triage`
   * picker is the Triage agent's, so a general-fenced key reads `reservedFor: 'triage'`
   * there and `reservedFor: 'coder'` in the general picker.
   */
  readonly reservedFor: AgentId | null;
};

/** A `(provider, model)` only unverified or invalid keys serve — shown, never selectable. */
export type UnavailableOffer = {
  readonly kind: 'unavailable';
  readonly provider: BYOKProvider;
  readonly model: string;
  readonly displayName: string;
  readonly keyCount: number;
  /** The better of its keys' states: `unverified` may still verify, `invalid` needs a fix. */
  readonly health: UnavailableHealth;
  /**
   * The reasoning capability any contributing catalogue entry reported. Mirrors
   * {@link ModelOffer.reasoning}; an unavailable offer is shown for awareness, not
   * picked, so the badge reads the same way.
   */
  readonly reasoning: ReasoningCapability | null;
  readonly reservedFor: AgentId | null;
};

/**
 * One entry of a picker, in display order: `automatic` first, then the current pin
 * (kept even when no key serves it any more — `gone`), then `empty` when nothing is
 * selectable, then the selectable models, then the unavailable ones.
 */
export type ModelPickerOption =
  | { readonly kind: 'automatic' }
  | { readonly kind: 'pinned'; readonly gone: false; readonly offer: ModelOffer | UnavailableOffer }
  | {
      readonly kind: 'pinned';
      readonly gone: true;
      readonly provider: string | null;
      readonly model: string;
      readonly displayName: string;
    }
  | ModelOffer
  | UnavailableOffer
  | { readonly kind: 'empty' };

export type ModelPickerInput = {
  /** The pin this picker edits. */
  readonly purpose: LlmRoutePurposeAny;
  /** The purpose's stored pin; null (or a null model) = the model is Automatic. */
  readonly currentPin: Pick<RouteRow, 'provider' | 'model'> | null;
};

type Contribution = { readonly key: PickerKey; readonly entry: PickerKeyModel };
type Pair = {
  readonly provider: BYOKProvider;
  readonly model: string;
  readonly contributions: Contribution[];
};

const SELECTABLE_RANK: Readonly<Record<SelectableHealth, number>> = {
  valid: 0,
  rate_limited: 1,
  credits_exhausted: 2,
};
const TIER_RANK: Readonly<Record<BYOKTier, number>> = { heavy: 0, smart: 1, general: 2, fast: 3 };
const UNTIERED_RANK = 4;
/**
 * The picker row's tier GROUP — heavy AND smart land at rank 0, the "Smart/Smartest"
 * top tier; general (1), fast (2), untiered (3) follow. Two tiers at the same rank
 * means the picker's order does not split them — the next tiebreaker (display name
 * then model) does. `TIER_RANK` is kept for `mostCommonTier`'s "stronger wins ties"
 * rule, which is a tie-breaker for the offer's DISPLAYED tier field, not for the
 * picker's sort.
 */
const SORT_TIER_GROUP: Readonly<Record<BYOKTier, number>> = {
  heavy: 0,
  smart: 0,
  general: 1,
  fast: 2,
};
const SORT_UNTIERED_GROUP = 3;
const KIND_ORDER: readonly BYOKKeyKind[] = ['metered', 'subscription'];

const cmp = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);
const tierRank = (tier: BYOKTier | null): number =>
  tier === null ? UNTIERED_RANK : TIER_RANK[tier];
const sortTierRank = (tier: BYOKTier | null): number =>
  tier === null ? SORT_UNTIERED_GROUP : SORT_TIER_GROUP[tier];

export function selectableHealth(status: BYOKStatus): SelectableHealth | null {
  return status === BYOKStatus.Valid ||
    status === BYOKStatus.RateLimited ||
    status === BYOKStatus.CreditsExhausted
    ? status
    : null;
}

/** The models a key serves: its live catalogue, plus its own model when the catalogue does not name it. */
function servedModels(key: PickerKey): readonly PickerKeyModel[] {
  const catalogue = key.models ?? [];
  const live = catalogue.filter((m) => modelActive(m));
  return catalogue.some((m) => m.model === key.model) ? live : [{ model: key.model }, ...live];
}

/** Every non-revoked key this lane admits, grouped by the `(provider, model)` it serves. */
function pairsFor(keys: readonly PickerKey[], purpose: LlmRoutePurposeAny): Pair[] {
  const byPair = new Map<string, Pair>();
  for (const key of keys) {
    if (key.status === BYOKStatus.Revoked) continue;
    if (key.purpose != null && !laneAdmits(key, purpose)) continue;
    for (const entry of servedModels(key)) {
      const id = `${key.provider}\u0000${entry.model}`;
      const pair = byPair.get(id) ?? {
        provider: key.provider,
        model: entry.model,
        contributions: [],
      };
      pair.contributions.push({ key, entry });
      byPair.set(id, pair);
    }
  }
  return [...byPair.values()];
}

function mostCommonTier(contributions: readonly Contribution[]): BYOKTier | null {
  const counts = new Map<BYOKTier, number>();
  for (const { entry } of contributions) {
    if (entry.tier != null) counts.set(entry.tier, (counts.get(entry.tier) ?? 0) + 1);
  }
  let best: BYOKTier | null = null;
  for (const [tier, n] of counts) {
    const lead = best === null ? 0 : (counts.get(best) ?? 0);
    if (n > lead || (n === lead && tierRank(tier) < tierRank(best))) best = tier;
  }
  return best;
}

/** The earliest parseable reset among the keys, as the key stated it; null = none known. */
function soonestReset(contributions: readonly Contribution[]): string | null {
  let soonest: { iso: string; at: number } | null = null;
  for (const { key } of contributions) {
    const iso = key.cooldown_until_iso ?? null;
    const at = iso === null ? Number.NaN : Date.parse(iso);
    if (iso !== null && !Number.isNaN(at) && (soonest === null || at < soonest.at)) {
      soonest = { iso, at };
    }
  }
  return soonest?.iso ?? null;
}

function smallestWindow(contributions: readonly Contribution[]): number | null {
  const windows = contributions.flatMap(({ entry }) => {
    const n = plausibleContextLength(entry.context_length);
    return n === undefined ? [] : [n];
  });
  return windows.length === 0 ? null : Math.min(...windows);
}

function cheapestPrice(contributions: readonly Contribution[]): ModelOffer['price'] {
  let best: ModelOffer['price'] = null;
  for (const { entry } of contributions) {
    // The catalogue's own both-or-nothing reader, over this one row.
    const rates = detectedModelRates({ models: [entry] }, entry.model);
    if (rates === undefined) continue;
    const cheaper =
      best === null ||
      rates.inputPerMtok < best.inputPerMtok ||
      (rates.inputPerMtok === best.inputPerMtok && rates.outputPerMtok < best.outputPerMtok);
    if (cheaper) best = rates;
  }
  return best;
}

function bestSelectable(contributions: readonly Contribution[]): SelectableHealth {
  let best: SelectableHealth = BYOKStatus.CreditsExhausted;
  for (const { key } of contributions) {
    const h = selectableHealth(key.status);
    if (h !== null && SELECTABLE_RANK[h] < SELECTABLE_RANK[best]) best = h;
  }
  return best;
}

function bestUnavailable(contributions: readonly Contribution[]): UnavailableHealth {
  return contributions.some(({ key }) => key.status === BYOKStatus.Unverified)
    ? BYOKStatus.Unverified
    : BYOKStatus.Invalid;
}

/**
 * The reasoning capability the offer advertises: the first contribution that carries
 * one wins. A contribution whose `reasoning` is `undefined` or `null` (no row, or a
 * row that was never probed) is skipped — they read the same as unknown — so a row
 * of one detected `supported: true` answers with that capability, and a row of all
 * unknowns answers `null`.
 */
function offerReasoning(contributions: readonly Contribution[]): ReasoningCapability | null {
  for (const { entry } of contributions) {
    if (entry.reasoning != null) return entry.reasoning;
  }
  return null;
}

function offerOf(pair: Pair, owner: AgentId): ModelOffer | UnavailableOffer {
  const selectable = pair.contributions.filter(({ key }) => selectableHealth(key.status) !== null);
  const serving = selectable.length > 0 ? selectable : pair.contributions;
  const base = {
    provider: pair.provider,
    model: pair.model,
    displayName: modelDisplayName(pair.model),
    keyCount: serving.length,
    reservedFor: serving.some(({ key }) => key.purpose != null) ? owner : null,
  };
  const reasoning = offerReasoning(serving);
  if (selectable.length === 0) {
    return { kind: 'unavailable', ...base, health: bestUnavailable(serving), reasoning };
  }
  const bestHealth = bestSelectable(serving);
  return {
    kind: 'model',
    ...base,
    bestHealth,
    soonestResetIso: bestHealth === BYOKStatus.Valid ? null : soonestReset(serving),
    tier: mostCommonTier(serving),
    kinds: KIND_ORDER.filter((kind) => serving.some(({ key }) => key.kind === kind)),
    contextLength: smallestWindow(serving),
    price: cheapestPrice(serving),
    reasoning,
  };
}

/**
 * The picker rows in display order: tier group first (Smart/Smartest
 * [heavy, smart] above general, fast, untiered), then health band, then provider,
 * then display name, then model id. Provider is now a tiebreaker rather than a
 * grouping — the tier group is the one grouping a person reads; grouping by
 * health would interleave a `rate_limited` offer with a `credits_exhausted` one
 * for the same provider, which is the wrong way to read a list.
 */
function orderModels(offers: readonly ModelOffer[]): ModelOffer[] {
  return [...offers].sort(
    (a, b) =>
      sortTierRank(a.tier) - sortTierRank(b.tier) ||
      SELECTABLE_RANK[a.bestHealth] - SELECTABLE_RANK[b.bestHealth] ||
      cmp(a.provider, b.provider) ||
      cmp(a.displayName, b.displayName) ||
      cmp(a.model, b.model),
  );
}

function orderUnavailable(offers: readonly UnavailableOffer[]): UnavailableOffer[] {
  return [...offers].sort(
    (a, b) =>
      cmp(a.provider, b.provider) || cmp(a.displayName, b.displayName) || cmp(a.model, b.model),
  );
}

type Pinned = Extract<ModelPickerOption, { kind: 'pinned' }>;

/** The current pin's entry, and the lists with its offer taken out so it is shown once. */
function takePin(
  currentPin: ModelPickerInput['currentPin'],
  models: ModelOffer[],
  unavailable: UnavailableOffer[],
): { pinned: Pinned | null; models: ModelOffer[]; unavailable: UnavailableOffer[] } {
  const model = currentPin?.model ?? null;
  if (currentPin === null || model === null) return { pinned: null, models, unavailable };
  const provider = currentPin.provider;
  const hits = (o: ModelOffer | UnavailableOffer): boolean =>
    o.model === model && (provider === null || o.provider === provider);
  const offer = models.find(hits) ?? unavailable.find(hits);
  if (offer === undefined) {
    const gone: Pinned = {
      kind: 'pinned',
      gone: true,
      provider,
      model,
      displayName: modelDisplayName(model),
    };
    return { pinned: gone, models, unavailable };
  }
  return {
    pinned: { kind: 'pinned', gone: false, offer },
    models: models.filter((o) => o !== offer),
    unavailable: unavailable.filter((o) => o !== offer),
  };
}

/**
 * The options behind one purpose's model picker, in display order (see
 * {@link ModelPickerOption}). `keys` is the ORG keys list; `currentPin` the purpose's
 * stored pin.
 *
 * The rows are the LANE's rows (`laneForPinPurpose`) and `reservedFor` is the PICKER's
 * owner (`AGENT_ROSTER.purpose`): the `triage` picker therefore offers the general
 * lane's keys and labels them for the TRIAGE agent, never the Coder. Every offer —
 * selectable or unavailable — carries the reasoning capability its catalogue entries
 * reported.
 */
export function modelPickerOptions(
  keys: readonly PickerKey[],
  input: ModelPickerInput,
): readonly ModelPickerOption[] {
  const owner = AGENT_ROSTER.purpose[input.purpose];
  const offers = pairsFor(keys, laneForPinPurpose(input.purpose)).map((pair) =>
    offerOf(pair, owner),
  );
  const { pinned, models, unavailable } = takePin(
    input.currentPin,
    orderModels(offers.filter((o): o is ModelOffer => o.kind === 'model')),
    orderUnavailable(offers.filter((o): o is UnavailableOffer => o.kind === 'unavailable')),
  );
  const pinSelectable = pinned !== null && !pinned.gone && pinned.offer.kind === 'model';
  return [
    { kind: 'automatic' },
    ...(pinned === null ? [] : [pinned]),
    ...(models.length === 0 && !pinSelectable ? [{ kind: 'empty' } as const] : []),
    ...models,
    ...unavailable,
  ];
}
